import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const savedUser = localStorage.getItem('user');
    const savedPerms = localStorage.getItem('permissions');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setPermissions(savedPerms ? JSON.parse(savedPerms) : []);
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  // #801: the login response's Permissions was previously the only source
  // ever used -- cached in localStorage and trusted for the entire session,
  // so a role/permission change made via Roles & Permissions never reached
  // anyone already logged in, not even across a page refresh. Poll for the
  // caller's own live permissions instead (same setInterval-in-a-useEffect
  // pattern AppLayout.js already uses for the notification unread count,
  // just on a longer interval since permission changes are far less
  // frequent than new notifications). GetUserPermissionsAsync re-derives
  // from the user's current RoleID server-side, so this also picks up a
  // role reassignment, not just an edit to the same role's permissions.
  useEffect(() => {
    if (!user) return undefined;
    const refresh = async () => {
      try {
        const res = await authAPI.getMyPermissions();
        if (res.data.Success) {
          setPermissions(res.data.Data || []);
          localStorage.setItem('permissions', JSON.stringify(res.data.Data || []));
        }
      } catch { /* keep the last-known permissions until the next tick succeeds */ }
    };
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const login = useCallback(async (credentials) => {
    const response = await authAPI.login(credentials);
    const { Data } = response.data;

    if (response.data.Success) {
      localStorage.setItem('accessToken', Data.AccessToken);
      localStorage.setItem('refreshToken', Data.RefreshToken);
      localStorage.setItem('user', JSON.stringify(Data.User));
      localStorage.setItem('permissions', JSON.stringify(Data.Permissions));
      setUser(Data.User);
      setPermissions(Data.Permissions);
      setMustChangePassword(!!Data.User?.MustChangePassword);
      return { success: true, mustChangePassword: !!Data.User?.MustChangePassword };
    }
    return { success: false, message: response.data.Message };
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.clear();
    setUser(null);
    setPermissions([]);
    setMustChangePassword(false);
  }, []);

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        u.MustChangePassword = false;
        localStorage.setItem('user', JSON.stringify(u));
        setUser(u);
      } catch {}
    }
  }, []);

  const hasPermission = useCallback((moduleCode, action = 'CanView') => {
    if (user?.RoleCode === 'SUPER_ADMIN') return true;
    const perm = permissions.find(p => p.ModuleCode === moduleCode);
    return perm ? perm[action] : false;
  }, [user, permissions]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  return (
    <AuthContext.Provider value={{
      user, permissions, loading, theme, mustChangePassword,
      login, logout, hasPermission, toggleTheme, clearMustChangePassword,
      isAdmin: user?.RoleCode === 'SUPER_ADMIN' || user?.RoleCode === 'ADMIN',
      isManager: ['SUPER_ADMIN','ADMIN','MANAGER'].includes(user?.RoleCode)
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
